import { createAsyncThunk } from '@reduxjs/toolkit'
import { tasksAPI, labelsAPI } from '../../utils/supabase'
import { refreshPerformanceData } from '../performanceSlice'

/**
 * Triggers a non-blocking performance data refresh when task completion status changes.
 * This ensures KanbanMetrics updates immediately without blocking the task update response.
 * Uses queueMicrotask to defer dispatch and avoid blocking the current call stack.
 */
const triggerPerformanceRefreshIfNeeded = (dispatch, updates, originalTask, userId) => {
  if (updates.completed !== undefined && updates.completed !== originalTask.completed) {
    // Defer to microtask queue to avoid blocking task update response
    queueMicrotask(() => {
      if (import.meta.env.DEV) {
        console.log('[Tasks] Refreshing performance data due to completion status change')
      }
      dispatch(refreshPerformanceData({ userId }))
    })
  }
}

// Async Thunks for Tasks
export const fetchTasks = createAsyncThunk(
  'tasks/fetchTasks',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await tasksAPI.getAllTasks(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createTask = createAsyncThunk(
  'tasks/createTask',
  async ({ userId, taskData }, { rejectWithValue, dispatch, getState }) => {
    // Extract labelIds from taskData to handle separately
    const { labelIds, ...taskDataWithoutLabels } = taskData

    // Generate optimistic ID for immediate UI update
    const optimisticId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const optimisticTask = {
      ...taskDataWithoutLabels,
      id: optimisticId,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      completed: false,
      archived: false,
      flagged: taskDataWithoutLabels.flagged || false,
      effort_level: taskDataWithoutLabels.effort_level || 2,
      labels: [], // Start with empty labels for optimistic update
      comments: [],
      _isOptimistic: true
    }

    try {
      // Dispatch optimistic update immediately
      dispatch({ type: 'tasks/createTask/optimistic', payload: optimisticTask })

      // Create task with labels atomically via database function
      // This eliminates the separate label assignment call and the order_position query
      const { data, error } = await tasksAPI.createTask(userId, taskDataWithoutLabels, labelIds || [])
      if (error) throw error

      // Database function returns complete task with labels already attached
      return {
        realTask: data,
        optimisticId,
        _wasOptimistic: true
      }
    } catch (error) {
      // Dispatch rollback action on error
      dispatch({ type: 'tasks/createTask/rollback', payload: optimisticId })
      return rejectWithValue(error.message)
    }
  }
)

export const updateTask = createAsyncThunk(
  'tasks/updateTask',
  async ({ taskId, updates }, { rejectWithValue, dispatch, getState }) => {
    // Get current task state for potential rollback
    const state = getState()
    const originalTask = state.tasks.tasks.find(task => task.id === taskId)

    if (!originalTask) {
      return rejectWithValue('Task not found')
    }

    // Extract labelIds from updates to handle separately
    const { labelIds, ...updatesWithoutLabels } = updates

    // Create optimistic update
    const optimisticTask = {
      ...originalTask,
      ...updatesWithoutLabels,
      updated_at: new Date().toISOString(),
      _isOptimisticUpdate: true
    }

    try {
      // Dispatch optimistic update immediately
      dispatch({
        type: 'tasks/updateTask/optimistic',
        payload: { task: optimisticTask, originalTask }
      })

      // If labelIds provided, use atomic function for task update + label sync
      if (labelIds !== undefined) {
        const userId = state.auth.user?.id

        // Validate userId before attempting label operations
        if (!userId) {
          throw new Error('User ID required for label operations')
        }

        // Use atomic database function for task update + label sync
        const { data, error } = await tasksAPI.updateTask(taskId, updatesWithoutLabels, labelIds, userId)
        if (error) throw error

        triggerPerformanceRefreshIfNeeded(dispatch, updates, originalTask, userId)

        // Database function returns complete task with labels already attached
        return {
          realTask: data,
          taskId,
          _wasOptimistic: true
        }
      }

      // No label updates, use standard update
      const { data, error } = await tasksAPI.updateTask(taskId, updatesWithoutLabels)
      if (error) throw error

      const userId = getState().auth.user?.id
      if (userId) {
        triggerPerformanceRefreshIfNeeded(dispatch, updates, originalTask, userId)
      }

      // Return task with existing labels preserved
      return {
        realTask: {
          ...data,
          labels: originalTask.labels || []
        },
        taskId,
        _wasOptimistic: true
      }
    } catch (error) {
      // Dispatch rollback action on error
      dispatch({
        type: 'tasks/updateTask/rollback',
        payload: { taskId, originalTask }
      })
      return rejectWithValue(error.message)
    }
  }
)

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const { error } = await tasksAPI.deleteTask(taskId)
      if (error) throw error
      return taskId
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const archiveTask = createAsyncThunk(
  'tasks/archiveTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const { data, error } = await tasksAPI.archiveTask(taskId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const unarchiveTask = createAsyncThunk(
  'tasks/unarchiveTask', 
  async (taskId, { rejectWithValue }) => {
    try {
      const { data, error } = await tasksAPI.unarchiveTask(taskId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const bulkArchiveTasks = createAsyncThunk(
  'tasks/bulkArchiveTasks',
  async (taskIds, { rejectWithValue }) => {
    try {
      const { data, error } = await tasksAPI.bulkArchiveTasks(taskIds)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Optimistic update for task plan with proper error handling and rollback
export const updateTaskPlanOptimistic = createAsyncThunk(
  'tasks/updateTaskPlanOptimistic',
  async ({ taskId, planUpdates }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get current task state for potential rollback
      const currentTask = getState().tasks.tasks.find(task => task.id === taskId)
      const previousPlan = currentTask?.plan ? { ...currentTask.plan } : null
      
      // Apply optimistic update immediately
      dispatch({
        type: 'tasks/updateTaskPlanOptimistic',
        payload: { taskId, planUpdates }
      })
      
      // Prepare the complete updated plan for API call
      const updatedPlan = {
        ...previousPlan,
        ...planUpdates,
        metadata: {
          created_at: previousPlan?.metadata?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      }
      
      // Make API call in background
      const { data, error } = await tasksAPI.updateTask(taskId, { plan: updatedPlan })
      
      if (error) {
        // Rollback optimistic update on failure
        dispatch({
          type: 'tasks/rollbackTaskPlanUpdate', 
          payload: { taskId, previousPlan }
        })
        throw error
      }
      
      // Return the updated task for final state sync
      return data
    } catch (error) {
      console.error('Plan update failed:', error)
      return rejectWithValue(error.message || 'Failed to update plan')
    }
  }
)

// Board Operations - moved from boardReducers.js
export const reorderTasksInColumn = createAsyncThunk(
  'tasks/reorderTasksInColumn',
  async ({ columnId, statusId, reorderedTasks }, { getState, rejectWithValue, dispatch }) => {
    // Get current state for potential rollback
    const state = getState()
    const userId = state.auth.user?.id
    
    if (!userId) {
      return rejectWithValue('User not authenticated')
    }

    // Validate input parameters
    if (!statusId || !Array.isArray(reorderedTasks) || reorderedTasks.length === 0) {
      return rejectWithValue('Invalid reordering parameters')
    }

    // Store original tasks for rollback
    const originalTasks = [...state.tasks.tasks]

    try {
      // Dispatch optimistic update immediately for instant UI feedback
      dispatch({
        type: 'tasks/reorderTasksInColumn/optimistic',
        payload: { statusId, reorderedTasks }
      })

      // Pass complete task objects with updated positions (using Redux state data)
      const tasksWithNewPositions = reorderedTasks.map((task, index) => ({
        ...task, // Complete task object from Redux state
        order_position: index + 1 // Updated position
      }))

      // Make API call with complete task data - no fetch needed!
      const { data: updatedTasks, error } = await tasksAPI.reorderTasksInColumn(userId, statusId, tasksWithNewPositions)
      
      if (error) {
        const errorMessage = typeof error === 'string' ? error : error.message || 'Failed to reorder tasks'
        throw new Error(errorMessage)
      }

      if (!updatedTasks || updatedTasks.length === 0) {
        throw new Error('No tasks were updated during reordering')
      }

      return { columnId, statusId, reorderedTasks: updatedTasks, _wasOptimistic: true }
    } catch (error) {
      console.error('Reordering failed:', error)
      
      // Rollback optimistic update on error
      dispatch({
        type: 'tasks/reorderTasksInColumn/rollback',
        payload: { originalTasks }
      })
      
      return rejectWithValue(error.message || 'Failed to reorder tasks')
    }
  }
)


// Tasks reducers
export const tasksReducers = {
  // Fetch Tasks
  [fetchTasks.pending]: (state) => {
    state.loading.tasks = true
    state.error.tasks = null
  },
  [fetchTasks.fulfilled]: (state, action) => {
    state.loading.tasks = false
    state.tasks = action.payload
    state.initialized.tasks = true

    // Import helper from tasksSlice for cache hydration
    // Hydrate labelAssignments cache from fetched tasks
    // This allows DELETE realtime events to work even after page refresh
    if (!state.labelAssignments) {
      state.labelAssignments = {}
    }

    action.payload.forEach(task => {
      if (task.labels && Array.isArray(task.labels)) {
        task.labels.forEach(label => {
          if (label.assignment_id) {
            state.labelAssignments[label.assignment_id] = {
              task_id: task.id,
              label_id: label.id
            }
          } else if (import.meta.env.DEV) {
            console.warn('[Tasks] Missing assignment_id in cache hydration:', {
              taskId: task.id,
              labelId: label.id,
              labelName: label.name
            })
          }
        })
      }
    })
  },
  [fetchTasks.rejected]: (state, action) => {
    state.loading.tasks = false
    state.error.tasks = action.payload
  },

  // Create Task
  [createTask.fulfilled]: (state, action) => {
    // Handle optimistic updates properly
    if (action.payload._wasOptimistic) {
      // Null check to prevent crashes
      if (!action.payload.realTask) {
        console.error('[Redux] Missing realTask in optimistic update payload')
        state.loading.tasks = false
        return
      }

      const realId = action.payload.realTask.id
      const optimisticId = action.payload.optimisticId

      // Filter out both optimistic task AND any duplicate real task (defense in depth)
      // This handles cases where realtime INSERT wasn't skipped (e.g., matching failed)
      state.tasks = state.tasks.filter(
        task => task.id !== optimisticId && task.id !== realId
      )

      // Remove internal flag before adding canonical task
      const { _isOptimistic, ...cleanTask } = action.payload.realTask
      state.tasks.unshift(cleanTask)

      if (import.meta.env.DEV) {
        console.log('[Redux] Replaced optimistic task with real task, cleaned up any duplicates')
      }
    } else {
      // Legacy: direct task payload (shouldn't happen with current implementation)
      state.tasks.unshift(action.payload)
    }
    state.loading.tasks = false
  },

  // Update Task
  [updateTask.fulfilled]: (state, action) => {
    // Handle optimistic updates properly
    if (action.payload._wasOptimistic) {
      // Replace optimistic task with real task
      const index = state.tasks.findIndex(task => task.id === action.payload.taskId)
      if (index !== -1) {
        state.tasks[index] = action.payload.realTask
      }
    } else {
      // Legacy: direct task payload (shouldn't happen with current implementation)
      const index = state.tasks.findIndex(task => task.id === action.payload.id)
      if (index !== -1) {
        state.tasks[index] = action.payload
      }
    }
  },

  // Delete Task
  [deleteTask.fulfilled]: (state, action) => {
    const taskId = action.payload

    // Clean up label assignment cache for this task
    if (state.labelAssignments) {
      Object.keys(state.labelAssignments).forEach(assignmentId => {
        if (state.labelAssignments[assignmentId].task_id === taskId) {
          delete state.labelAssignments[assignmentId]
        }
      })
    }

    state.tasks = state.tasks.filter(task => task.id !== taskId)
  },

  // Archive Task
  [archiveTask.fulfilled]: (state, action) => {
    const index = state.tasks.findIndex(task => task.id === action.payload.id)
    if (index !== -1) {
      state.tasks[index] = action.payload
    }
  },

  // Unarchive Task
  [unarchiveTask.fulfilled]: (state, action) => {
    const index = state.tasks.findIndex(task => task.id === action.payload.id)
    if (index !== -1) {
      state.tasks[index] = action.payload
    }
  },

  // Bulk Archive Tasks
  [bulkArchiveTasks.fulfilled]: (state, action) => {
    action.payload.forEach(archivedTask => {
      const index = state.tasks.findIndex(task => task.id === archivedTask.id)
      if (index !== -1) {
        state.tasks[index] = archivedTask
      }
    })
  },

  // Reorder Tasks in Column
  [reorderTasksInColumn.fulfilled]: (state, action) => {
    if (action.payload._wasOptimistic) {
      // Replace optimistic tasks with real server data
      const { reorderedTasks } = action.payload
      
      reorderedTasks.forEach(reorderedTask => {
        const index = state.tasks.findIndex(task => task.id === reorderedTask.id)
        if (index !== -1) {
          // Update the existing task with new order_position and any other updated data
          state.tasks[index] = {
            ...state.tasks[index],
            ...reorderedTask
          }
        }
      })
    }
  },
  [reorderTasksInColumn.rejected]: (state, action) => {
    state.error.tasks = action.payload
  }
}

// NOTE: Optimistic update reducers moved to tasksSlice.js extraReducers
// to fix action type double-prefixing issue with createSlice reducers object
